import React from "react";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import {
  formatDepartureDateTime,
  formatMoney,
  getTravelQuotationDisplayLabel,
  getTravelQuotationProjectLabel,
  getStatusClass,
  getTotalAmount,
  truncate,
} from "./TravelExpenseApprovalUtils";

const canEditRecord = (status) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  return normalizedStatus === "pending" || normalizedStatus === "rejected";
};

const canDeleteRecord = (status) =>
  String(status || "").trim().toLowerCase() === "pending";

const TravelExpenseTable = ({
  records,
  loading = false,
  deletingId = "",
  onEdit,
  onDelete,
}) => {
  return (
    <div className="mt-8 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            คำขอของฉัน
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            แสดงรายการเบิกค่าเดินทางของผู้ใช้ปัจจุบัน พร้อมแก้ไขหรือลบได้จากที่นี่
          </p>
        </div>
        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
          ทั้งหมด {records.length} รายการ
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">วันที่เดินทาง</th>
              <th className="px-4 py-3 text-left font-semibold">QT / โครงการ</th>
              <th className="px-4 py-3 text-left font-semibold">เส้นทาง</th>
              <th className="px-4 py-3 text-left font-semibold">หมายเหตุ</th>
              <th className="px-4 py-3 text-right font-semibold">จำนวนเงินรวม</th>
              <th className="px-4 py-3 text-center font-semibold">สถานะ</th>
              <th className="px-4 py-3 text-center font-semibold">จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  กำลังโหลดรายการ...
                </td>
              </tr>
            ) : records.length > 0 ? (
              records.map((item) => {
                const editable = canEditRecord(item.status);
                const deletable = canDeleteRecord(item.status);
                const quotationLabel = getTravelQuotationDisplayLabel(item);
                const projectLabel = getTravelQuotationProjectLabel(item);

                return (
                  <tr
                    key={item._id}
                    className="border-t border-gray-100 dark:border-gray-700 align-top"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-200">
                      {formatDepartureDateTime(item.departureDateTime)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                      <div className="font-medium break-words">
                        {truncate(quotationLabel, 48)}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {truncate(projectLabel, 48)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                      <div>{truncate(item.origin || "-", 34)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        ถึง {truncate(item.destination || "-", 34)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                      {truncate(item.note || "-", 48)}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {formatMoney(getTotalAmount(item))} บาท
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClass(
                          item.status
                        )}`}
                      >
                        {item.status || "Pending"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit?.(item)}
                          disabled={!editable}
                          className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition ${
                            editable
                              ? "border border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 dark:border-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-300"
                              : "cursor-not-allowed border border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500"
                          }`}
                        >
                          <FiEdit2 size={14} />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete?.(item)}
                          disabled={!deletable || deletingId === item._id}
                          className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition ${
                            deletable && deletingId !== item._id
                              ? "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300"
                              : "cursor-not-allowed border border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500"
                          }`}
                        >
                          <FiTrash2 size={14} />
                          {deletingId === item._id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  ยังไม่มีรายการเบิกค่าเดินทางของคุณ
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TravelExpenseTable;
